import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/api';
import './SalaryPayrollPage.css';

type PayrollEmployee = {
  appUserId: string;
  employeeName: string;
  departmentName: string;
  position: string;
  method: 'fixed-plus-revenue' | 'hourly-coefficient' | 'revenue-weighted-hours';
  workedHours: number;
  hourlyRate: number;
  evaluationCoefficient: number;
  evaluationCoefficientSource: string;
  salaryHours: number;
  baseSalary: number;
  fixedBonus: number;
  bonusPercent: number;
  bonus: number;
  fixedPenalty: number;
  penaltyPercent: number;
  penalty: number;
  fixedMonthlyRate: number;
  revenuePercent: number;
  monthlyRevenue: number;
  amount: number;
  policySource: string;
  policyEffectiveYear: number;
  policyEffectiveMonth: number;
  adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
};

type PayrollPeriod = {
  exists: boolean;
  year: number;
  month: number;
  status: 'draft' | 'approved' | 'closed';
  version: number;
  monthlyRevenue: number;
  salaryFundPercent: number;
  salaryFund: number;
  totalSalaryHours: number;
  hourRate: number;
  total: number;
  issues: string[];
  employees: PayrollEmployee[];
  approvedAtUtc?: string;
  approvedByUsername?: string;
  closedAtUtc?: string;
  closedByUsername?: string;
};

type PayrollPolicy = {
  id: number;
  scope: 'global' | 'department' | 'employee';
  scopeKey: string;
  departmentId?: number;
  appUserId?: string;
  effectiveYear: number;
  effectiveMonth: number;
  revision: number;
  calculationMethod: 'fixed-plus-revenue' | 'hourly-coefficient' | 'revenue-weighted-hours';
  fixedMonthlyRate: number;
  revenuePercent: number;
  hourlyRateOverride?: number | null;
  evaluationCoefficientOverride?: number | null;
};
type PolicyOptions = {
  departments: Array<{ id: number; name: string }>;
  employees: Array<{ id: string; name: string; departmentId?: number; departmentName: string }>;
  policies: PayrollPolicy[];
};
type DraftEmployeeInput = {
  fixedBonus: string;
  bonusPercent: string;
  fixedPenalty: string;
  penaltyPercent: string;
};

const currency = new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH' });
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const newIdempotencyKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);
const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  return fallback;
};

const SalaryPayrollPage: React.FC = () => {
  const [month, setMonth] = useState(currentMonth);
  const [policyMonth, setPolicyMonth] = useState(currentMonth);
  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [adjustmentEmployeeId, setAdjustmentEmployeeId] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentKey, setAdjustmentKey] = useState('');
  const [draftInputEmployeeId, setDraftInputEmployeeId] = useState('');
  const [draftInputs, setDraftInputs] = useState<Record<string, DraftEmployeeInput>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [policyOptions, setPolicyOptions] = useState<PolicyOptions | null>(null);
  const [policyScope, setPolicyScope] = useState<'global' | 'department' | 'employee'>('global');
  const [policyDepartmentId, setPolicyDepartmentId] = useState('');
  const [policyEmployeeId, setPolicyEmployeeId] = useState('');
  const [policyMethod, setPolicyMethod] = useState<'fixed-plus-revenue' | 'hourly-coefficient' | 'revenue-weighted-hours'>('hourly-coefficient');
  const [policyFixedRate, setPolicyFixedRate] = useState('0');
  const [policyRevenuePercent, setPolicyRevenuePercent] = useState('0');
  const [policyHourlyRate, setPolicyHourlyRate] = useState('');
  const [policyCoefficientOverride, setPolicyCoefficientOverride] = useState('');
  const [yearText, monthText] = month.split('-');
  const year = Number(yearText);
  const monthNumber = Number(monthText);
  const [policyYearText, policyMonthText] = policyMonth.split('-');
  const policyYear = Number(policyYearText);
  const policyMonthNumber = Number(policyMonthText);
  const activeGlobalPolicy = policyOptions?.policies
    .filter(policy => policy.scope === 'global' &&
      (policy.effectiveYear < policyYear ||
        (policy.effectiveYear === policyYear && policy.effectiveMonth <= policyMonthNumber)))
    .sort((left, right) =>
      right.effectiveYear - left.effectiveYear ||
      right.effectiveMonth - left.effectiveMonth ||
      right.revision - left.revision)[0];
  const weightedFundActive = activeGlobalPolicy?.calculationMethod === 'revenue-weighted-hours';

  const load = useCallback(async (active: { current: boolean }) => {
    if (!year || monthNumber < 1 || monthNumber > 12) return;
    setBusy(true);
    setError('');
    try {
      const [periodResponse, accessResponse, policiesResponse] = await Promise.all([
        api.get<PayrollPeriod>(`/salary-payroll/${year}/${monthNumber}`),
        api.get<{ editPermissions: string[] }>('/users/me/access'),
        api.get<PolicyOptions>('/salary-payroll/policies'),
      ]);
      if (!active.current) return;
      setPeriod(periodResponse.data);
      setCanEdit(accessResponse.data.editPermissions.includes('page.management.salary'));
      setPolicyOptions(policiesResponse.data);
      setAdjustmentEmployeeId(periodResponse.data.employees[0]?.appUserId ?? '');
      setDraftInputEmployeeId(periodResponse.data.employees[0]?.appUserId ?? '');
      setDraftInputs(Object.fromEntries(periodResponse.data.employees.map(employee => [
        employee.appUserId,
        {
          fixedBonus: String(employee.fixedBonus),
          bonusPercent: String(employee.bonusPercent),
          fixedPenalty: String(employee.fixedPenalty),
          penaltyPercent: String(employee.penaltyPercent),
        },
      ])));
    } catch (loadError) {
      if (active.current) setError(errorMessage(loadError, 'Не вдалося завантажити зарплатний період.'));
    } finally {
      if (active.current) setBusy(false);
    }
  }, [year, monthNumber]);

  useEffect(() => {
    const active = { current: true };
    void load(active);
    return () => { active.current = false; };
  }, [load]);

  const reload = async () => {
    const active = { current: true };
    await load(active);
  };

  const createDraft = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.post(`/salary-payroll/${year}/${monthNumber}`, {});
      await reload();
      setNotice('Чернетку створено. Перевірте попередній розрахунок перед погодженням.');
    } catch (actionError) {
      setError(errorMessage(actionError, 'Не вдалося створити чернетку.'));
    } finally {
      setBusy(false);
    }
  };

  const transition = async (action: 'approve' | 'close') => {
    if (!period?.exists) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.post(`/salary-payroll/${year}/${monthNumber}/${action}`, { version: period.version });
      await reload();
      setNotice(action === 'approve' ? 'Зарплату погоджено та зафіксовано.' : 'Період закрито; зміни надалі оформлюйте коригуванням.');
    } catch (actionError) {
      setError(errorMessage(actionError, 'Не вдалося змінити статус періоду.'));
    } finally {
      setBusy(false);
    }
  };

  const addAdjustment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!period?.exists || !adjustmentEmployeeId) return;
    setBusy(true);
    setError('');
    setNotice('');
    const key = adjustmentKey || newIdempotencyKey();
    setAdjustmentKey(key);
    try {
      await api.post(`/salary-payroll/${year}/${monthNumber}/adjustments`, {
        appUserId: adjustmentEmployeeId,
        amount: Number(adjustmentAmount),
        reason: adjustmentReason,
      }, { headers: { 'Idempotency-Key': key } });
      await reload();
      setAdjustmentAmount('');
      setAdjustmentReason('');
      setAdjustmentKey('');
      setNotice('Коригування додано окремим записом.');
    } catch (actionError) {
      setError(errorMessage(actionError, 'Не вдалося додати коригування.'));
    } finally {
      setBusy(false);
    }
  };

  const saveDraftEmployeeInput = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!period?.exists || period.status !== 'draft' || !draftInputEmployeeId) return;
    const values = draftInputs[draftInputEmployeeId] ?? {
      fixedBonus: '0',
      bonusPercent: '0',
      fixedPenalty: '0',
      penaltyPercent: '0',
    };
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.put(`/salary-payroll/${year}/${monthNumber}/employees/${encodeURIComponent(draftInputEmployeeId)}/adjustments`, {
        version: period.version,
        fixedBonus: Number(values.fixedBonus),
        bonusPercent: Number(values.bonusPercent),
        fixedPenalty: Number(values.fixedPenalty),
        penaltyPercent: Number(values.penaltyPercent),
      });
      await reload();
      setNotice('Місячні бонуси та штрафи збережено в чернетці; вони не повторюються в інших місяцях.');
    } catch (actionError) {
      setError(errorMessage(actionError, 'Не вдалося зберегти місячні бонуси та штрафи.'));
    } finally {
      setBusy(false);
    }
  };

  const savePolicy = async (event: React.FormEvent) => {
    event.preventDefault();
    if (policyScope === 'department' && !policyDepartmentId) return;
    if (policyScope === 'employee' && !policyEmployeeId) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.put('/salary-payroll/policies', {
        scope: policyScope,
        departmentId: policyScope === 'department' ? Number(policyDepartmentId) : null,
        appUserId: policyScope === 'employee' ? policyEmployeeId : null,
        effectiveYear: policyYear,
        effectiveMonth: policyMonthNumber,
        calculationMethod: policyMethod,
        fixedMonthlyRate: Number(policyFixedRate),
        revenuePercent: Number(policyRevenuePercent),
        hourlyRateOverride: policyHourlyRate === '' ? null : Number(policyHourlyRate),
        evaluationCoefficientOverride: policyCoefficientOverride === '' ? null : Number(policyCoefficientOverride),
      });
      await reload();
      setNotice(`Правило збережено з ${String(policyMonthNumber).padStart(2, '0')}.${policyYear}. Воно не змінює вже погоджені нарахування.`);
    } catch (saveError) {
      setError(errorMessage(saveError, 'Не вдалося зберегти правило нарахування.'));
    } finally {
      setBusy(false);
    }
  };

  const policyScopeLabel = (policy: PayrollPolicy) => (
    policy.scope === 'global' ? 'Для всіх підрозділів' :
      policy.scope === 'department'
        ? `Підрозділ: ${policyOptions?.departments.find(item => item.id === policy.departmentId)?.name ?? policy.scopeKey}`
        : `Працівник: ${policyOptions?.employees.find(item => item.id === policy.appUserId)?.name ?? policy.scopeKey}`
  );

  return (
    <main className="salary-payroll">
      <header className="salary-payroll__header">
        <div>
          <h1>Місячна зарплата працівників</h1>
          <p>Доступні методи: ставка + частка виторгу, погодинна ставка × години × коефіцієнт або розподіл фонду за зваженими годинами.</p>
          <p>Для фондового методу коефіцієнт береться з HR-оцінювання або заданого тут перевизначення. Бонуси й штрафи задаються окремо для кожної місячної чернетки; Весільні Зали розраховуються окремо.</p>
        </div>
        <label>Місяць зарплати<input aria-label="Місяць зарплати" type="month" value={month} onChange={event => setMonth(event.target.value)} /></label>
      </header>
      {error && <p role="alert" className="salary-payroll__error">{error}</p>}
      {notice && <p role="status" className="salary-payroll__notice">{notice}</p>}
      {busy && <p>Оновлення періоду…</p>}
      {period && (
        <>
          <section className="salary-payroll__summary">
            <p><strong>Статус:</strong> {period.status === 'draft' ? 'Чернетка' : period.status === 'approved' ? 'Погоджено' : 'Закрито'}</p>
            <p><strong>Виторг, знімок/джерело:</strong> {currency.format(period.monthlyRevenue)}</p>
            {(period.salaryFundPercent > 0 || period.employees.some(employee => employee.method === 'revenue-weighted-hours')) && <>
              <p><strong>Фонд зарплати:</strong> {currency.format(period.salaryFund)} ({period.salaryFundPercent.toFixed(2)}% виторгу)</p>
              <p><strong>Зважені години:</strong> {period.totalSalaryHours.toFixed(2)} · <strong>Годинна ставка фонду:</strong> {currency.format(period.hourRate)}</p>
            </>}
            <p><strong>Фонд зафіксованої зарплати:</strong> {currency.format(period.total)}</p>
            {period.approvedByUsername && <p>Погодив(-ла): {period.approvedByUsername}</p>}
            {period.closedByUsername && <p>Закрив(-ла): {period.closedByUsername}</p>}
          </section>
          <section className="salary-payroll__policy-settings">
            <h2>Правила нарахування</h2>
            <p>Пріоритет: персональне правило → правило підрозділу → загальне. Без спеціального правила зберігаються поточні налаштування працівника та ставки посади. Правило застосовується з обраного місяця; погоджені знімки не змінюються.</p>
            {weightedFundActive && policyScope !== 'global' && <p>Глобальний метод розподілу фонду діє для всіх звичайних підрозділів. Правило підрозділу або працівника тут задає перевизначення HR-коефіцієнта; метод і відсоток фонду визначаються глобальним правилом.</p>}
            {canEdit && (
              <form onSubmit={event => void savePolicy(event)}>
                <label>Для кого
                  <select value={policyScope} onChange={event => {
                    const scope = event.target.value as typeof policyScope;
                    setPolicyScope(scope);
                    if (scope !== 'global' && policyMethod === 'revenue-weighted-hours') setPolicyMethod('hourly-coefficient');
                  }}>
                    <option value="global">Усі звичайні підрозділи</option>
                    <option value="department">Окремий підрозділ</option>
                    <option value="employee">Окремий працівник</option>
                  </select>
                </label>
                {policyScope === 'department' && <label>Підрозділ
                  <select required value={policyDepartmentId} onChange={event => setPolicyDepartmentId(event.target.value)}>
                    <option value="">Оберіть підрозділ</option>
                    {policyOptions?.departments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>}
                {policyScope === 'employee' && <label>Працівник
                  <select required value={policyEmployeeId} onChange={event => setPolicyEmployeeId(event.target.value)}>
                    <option value="">Оберіть працівника</option>
                    {policyOptions?.employees.map(item => <option key={item.id} value={item.id}>{item.name} — {item.departmentName || 'без підрозділу'}</option>)}
                  </select>
                </label>}
                <label>Період початку дії<input required aria-label="Період початку дії правила" type="month" value={policyMonth} onChange={event => setPolicyMonth(event.target.value)} /></label>
                <label>Метод
                  <select value={policyMethod} onChange={event => setPolicyMethod(event.target.value as typeof policyMethod)}>
                    <option value="hourly-coefficient">Години × ставка посади × коефіцієнт оцінювання</option>
                    <option value="fixed-plus-revenue">Місячна ставка + відсоток виторгу</option>
                    {policyScope === 'global' && <option value="revenue-weighted-hours">Фонд від виторгу ÷ загальні зважені години</option>}
                  </select>
                </label>
                {policyMethod === 'revenue-weighted-hours' && policyScope === 'global' && (
                  <p>Використовується збережена місячна сума виторгу як тестова база: фонд = виторг × відсоток; години працівника = відпрацьовані години × коефіцієнт.</p>
                )}
                {policyMethod === 'fixed-plus-revenue' && <>
                  <label>Фіксована сума за місяць, ₴<input type="number" min="0" step="0.01" value={policyFixedRate} onChange={event => setPolicyFixedRate(event.target.value)} /></label>
                  <label>Відсоток місячного виторгу<input type="number" min="0" max="100" step="0.01" value={policyRevenuePercent} onChange={event => setPolicyRevenuePercent(event.target.value)} /></label>
                </>}
                {policyMethod === 'revenue-weighted-hours' && policyScope === 'global' && <label>Відсоток фонду від виторгу<input type="number" min="0" max="100" step="0.01" value={policyRevenuePercent} onChange={event => setPolicyRevenuePercent(event.target.value)} /></label>}
                {policyMethod === 'hourly-coefficient' && <label>Погодинна ставка, ₴ (порожньо — за посадою)<input type="number" min="0" step="0.01" value={policyHourlyRate} onChange={event => setPolicyHourlyRate(event.target.value)} /></label>}
                <label>Перевизначення коефіцієнта HR (порожньо — успадкувати)<input type="number" min="0" step="0.000001" value={policyCoefficientOverride} onChange={event => setPolicyCoefficientOverride(event.target.value)} /></label>
                <button type="submit" disabled={busy}>Зберегти правило</button>
              </form>
            )}
            <div className="salary-payroll__policy-list">
              <h3>Налаштовані правила</h3>
              {policyOptions?.policies.map(policy => (
                <p key={policy.id}>
                  <strong>{policyScopeLabel(policy)}</strong> · з {String(policy.effectiveMonth).padStart(2, '0')}.{policy.effectiveYear} · версія {policy.revision} · {policy.calculationMethod === 'hourly-coefficient' ? 'погодинна ставка × коефіцієнт' : policy.calculationMethod === 'revenue-weighted-hours' ? `${policy.revenuePercent}% фонду від виторгу` : `₴${policy.fixedMonthlyRate.toFixed(2)} + ${policy.revenuePercent}% виторгу`}
                  {policy.evaluationCoefficientOverride != null && ` · коефіцієнт ${policy.evaluationCoefficientOverride}`}
                </p>
              ))}
              {!policyOptions?.policies.length && <p>Спеціальних правил ще не задано.</p>}
            </div>
            {canEdit && period.exists && period.status === 'draft' && (
              <form className="salary-payroll__draft-adjustments" onSubmit={saveDraftEmployeeInput}>
                <h2>Бонуси та штрафи за цей місяць</h2>
                <p>Разові значення для вибраного працівника. Відсотки застосовуються до BaseSalary + фіксований бонус − фіксований штраф.</p>
                <label>Працівник<select required value={draftInputEmployeeId} onChange={event => setDraftInputEmployeeId(event.target.value)}>
                  {period.employees.map(employee => <option key={employee.appUserId} value={employee.appUserId}>{employee.employeeName}</option>)}
                </select></label>
                {(['fixedBonus', 'bonusPercent', 'fixedPenalty', 'penaltyPercent'] as const).map(field => (
                  <label key={field}>{field === 'fixedBonus' ? 'Фіксований бонус, ₴' :
                    field === 'bonusPercent' ? 'Бонус, %' :
                      field === 'fixedPenalty' ? 'Фіксований штраф, ₴' : 'Штраф, %'}
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={draftInputs[draftInputEmployeeId]?.[field] ?? '0'}
                      onChange={event => setDraftInputs(previous => ({
                        ...previous,
                        [draftInputEmployeeId]: {
                          ...(previous[draftInputEmployeeId] ?? { fixedBonus: '0', bonusPercent: '0', fixedPenalty: '0', penaltyPercent: '0' }),
                          [field]: event.target.value,
                        },
                      }))}
                    />
                  </label>
                ))}
                <button type="submit" disabled={busy || !draftInputEmployeeId}>Зберегти для цього місяця</button>
              </form>
            )}
          </section>
          {period.issues.length > 0 && (
            <section className="salary-payroll__issues" aria-label="Проблеми розрахунку">
              <h2>Потрібно виправити перед погодженням</h2>
              <ul>{period.issues.map((issue, index) => <li key={`${issue}-${index}`}>{issue}</li>)}</ul>
            </section>
          )}
          <div className="salary-payroll__table-wrap">
            <table>
              <thead><tr><th>Працівник</th><th>Підрозділ / посада</th><th>Метод</th><th>Відпрацьовано / зважено</th><th>Ставка / коефіцієнт</th><th>База</th><th>Бонус</th><th>Штраф</th><th>Разом</th></tr></thead>
              <tbody>
                {period.employees.map(employee => (
                  <tr key={employee.appUserId}>
                    <td>{employee.employeeName}</td>
                    <td>{employee.departmentName || '—'} / {employee.position || '—'}</td>
                    <td>{employee.method === 'hourly-coefficient' ? 'Погодинна × коефіцієнт' : employee.method === 'revenue-weighted-hours' ? 'Фонд ÷ зважені години' : 'Місячна ставка + % виторгу'}<small>{employee.policySource === 'legacy' ? 'Поточні налаштування працівника' : `Правило: ${employee.policySource}`}</small></td>
                    <td>{employee.workedHours.toFixed(2)}{employee.method === 'revenue-weighted-hours' ? ` / ${employee.salaryHours.toFixed(2)}` : ''}</td>
                    <td>{employee.method === 'hourly-coefficient'
                      ? `${currency.format(employee.hourlyRate)} × ${employee.evaluationCoefficient.toFixed(4)}`
                      : employee.method === 'revenue-weighted-hours'
                        ? `коеф. ${employee.evaluationCoefficient.toFixed(4)} (${employee.evaluationCoefficientSource === 'manual' ? 'ручний' : 'HR'})`
                      : `${currency.format(employee.fixedMonthlyRate)} + ${employee.revenuePercent.toFixed(2)}%`}</td>
                    <td>{currency.format(employee.baseSalary)}</td>
                    <td>{currency.format(employee.bonus)}<small>{currency.format(employee.fixedBonus)} + {employee.bonusPercent}%</small></td>
                    <td>{currency.format(employee.penalty)}<small>{currency.format(employee.fixedPenalty)} + {employee.penaltyPercent}%</small></td>
                    <td>{currency.format(employee.amount)}</td>
                  </tr>
                ))}
                {period.employees.length === 0 && <tr><td colSpan={9}>За цей період немає працівників для розрахунку.</td></tr>}
              </tbody>
            </table>
          </div>
          {canEdit && !period.exists && <button type="button" disabled={busy} onClick={() => void createDraft()}>Створити чернетку</button>}
          {canEdit && period.exists && period.status === 'draft' && (
            <button type="button" disabled={busy || period.issues.length > 0 || period.employees.length === 0} onClick={() => void transition('approve')}>Погодити й зафіксувати</button>
          )}
          {canEdit && period.status === 'approved' && (
            <button type="button" disabled={busy} onClick={() => void transition('close')}>Закрити період</button>
          )}
          {canEdit && period.status === 'closed' && (
            <form className="salary-payroll__adjustment" onSubmit={addAdjustment}>
              <h2>Коригування після закриття</h2>
              <label>Працівник<select required value={adjustmentEmployeeId} onChange={event => { setAdjustmentEmployeeId(event.target.value); setAdjustmentKey(''); }}>
                {period.employees.map(employee => <option key={employee.appUserId} value={employee.appUserId}>{employee.employeeName}</option>)}
              </select></label>
              <label>Сума (+ / −)<input required type="number" step="0.01" value={adjustmentAmount} onChange={event => { setAdjustmentAmount(event.target.value); setAdjustmentKey(''); }} /></label>
              <label>Причина<input required maxLength={1000} value={adjustmentReason} onChange={event => { setAdjustmentReason(event.target.value); setAdjustmentKey(''); }} /></label>
              <button type="submit" disabled={busy || !adjustmentEmployeeId}>Додати коригування</button>
            </form>
          )}
          {period.employees.some(employee => employee.adjustments.length > 0) && (
            <section className="salary-payroll__adjustments">
              <h2>Окремі коригування</h2>
              {period.employees.flatMap(employee => employee.adjustments.map((adjustment, index) => (
                <p key={`${employee.appUserId}-${index}`}>{employee.employeeName}: {currency.format(adjustment.amount)} — {adjustment.reason} ({adjustment.createdByUsername})</p>
              )))}
            </section>
          )}
        </>
      )}
    </main>
  );
};

export default SalaryPayrollPage;
